import type { StarGridText, StarGridTextKey } from 'src/components/star-grid/hooks/useStarGridText';
import type {
  NavigationReport,
  StarGridCustomNavGroup,
  StarGridNavGroup,
  StarGridNavGroupPreference
} from 'src/components/star-grid/navigation.types';

/** Section for a report whose template cannot be determined — which includes every hand-built one. */
export const UNGROUPED_NAV_GROUP_ID = 'other';

/** Namespaces a user-created section so its id can never collide with a template id. */
export const CUSTOM_NAV_GROUP_PREFIX = 'custom:';

/** The whole Reports block, collapsed like a section; namespaced so no group id can take it. */
export const REPORTS_NAV_BLOCK_ID = 'nav:reports';

export interface StarGridNavGroupDefinition {
  readonly id: string;
  readonly labelKey: StarGridTextKey;
}

export interface StarGridNavGroupOption {
  readonly value: string;
  readonly label: string;
}

/** One row of the Organize Reports list: a report and the section it currently sits in. */
export interface StarGridNavAssignment {
  readonly report: NavigationReport;
  readonly groupId: string;
}

/**
 * The template sections, in the server's own template order (`report_engine/registry.py: TEMPLATE_ORDER`).
 *
 * Mirrored rather than fetched: the nav has to lay out before any engine round trip would answer, and
 * this order tracks the deck config rather than varying per request.
 */
export const TEMPLATE_NAV_GROUPS: readonly StarGridNavGroupDefinition[] = [
  { id: 'ds_pnl', labelKey: 'navGroupDsPnl' },
  { id: 'product', labelKey: 'navGroupProduct' },
  { id: 'gross_profit', labelKey: 'navGroupGrossProfit' },
  { id: 'opex', labelKey: 'navGroupOpex' },
  { id: 'headcount', labelKey: 'navGroupHeadcount' },
  { id: 'program_financials', labelKey: 'navGroupProgramFinancials' }
];

/** The catch-all. Listed after every template section unless the user moves it. */
export const UNGROUPED_NAV_GROUP: StarGridNavGroupDefinition = { id: UNGROUPED_NAV_GROUP_ID, labelKey: 'navGroupOther' };

/**
 * The section order to lay out with until `GET /report-nav/preference` answers with the server's own.
 *
 * The service PUBLISHES the default order (`report_nav.store.published_group_order`), and that is the
 * authority — but the nav has to render before the first round trip resolves, and this list is the
 * same `TEMPLATE_ORDER` mirrored above, so starting here and adopting the server's answer costs the
 * user no visible reflow in the normal case.
 */
export const PUBLISHED_NAV_GROUP_ORDER: readonly string[] = TEMPLATE_NAV_GROUPS.map((group) => group.id);

const TEMPLATE_GROUP_IDS: ReadonlySet<string> = new Set(TEMPLATE_NAV_GROUPS.map((group) => group.id));

/**
 * `FPA Deck Report` pin → template, mirroring `report_engine/registry.py: DECK_REPORT_TEMPLATES`.
 *
 * A FALLBACK for a row that carries no `template_id` — an older report saved before the list served
 * one. It reaches only the two templates that share the `FPA Deck` cube; a report built on one of the
 * four detail cubes has nothing else on its row to key off, so it lands in the catch-all.
 */
const TEMPLATE_ID_BY_DECK_REPORT: Readonly<Record<string, string>> = {
  'ww report': 'ds_pnl',
  'country report': 'ds_pnl',
  'regional report': 'ds_pnl',
  'product report': 'product'
};

export const EMPTY_NAV_GROUP_PREFERENCE: StarGridNavGroupPreference = {
  groupByReportId: {},
  order: [],
  collapsed: [],
  customGroups: []
};

/** The section a report falls in with no user override. */
export const defaultNavGroupId = (report: NavigationReport): string => {
  const templateId = (report.template_id ?? '').trim();
  if (TEMPLATE_GROUP_IDS.has(templateId)) return templateId;
  return TEMPLATE_ID_BY_DECK_REPORT[(report.deck_report ?? '').trim().toLowerCase()] ?? UNGROUPED_NAV_GROUP_ID;
};

const stringList = (value: unknown): string[] => (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : []);

const isCustomGroup = (value: unknown): value is StarGridCustomNavGroup =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StarGridCustomNavGroup).id === 'string' &&
  typeof (value as StarGridCustomNavGroup).label === 'string';

const assignmentMap = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [reportId, groupId] of Object.entries(value)) {
    if (typeof groupId === 'string') out[reportId] = groupId;
  }
  return out;
};

/**
 * A preference read back from the service, re-validated field by field.
 *
 * The service stores the arrangement as ONE OPAQUE document and deliberately does not model its
 * fields, so what comes back is arbitrary JSON rather than this interface — including whatever an
 * older build of this app wrote. Anything unrecognised is dropped and that part of the nav falls back
 * to the published default. This function is the only definition of the document's shape.
 */
export const normalizeNavGroupPreference = (raw: unknown): StarGridNavGroupPreference => {
  if (!raw || typeof raw !== 'object') return EMPTY_NAV_GROUP_PREFERENCE;
  const candidate = raw as Partial<Record<keyof StarGridNavGroupPreference, unknown>>;
  return {
    groupByReportId: assignmentMap(candidate.groupByReportId),
    order: stringList(candidate.order),
    collapsed: stringList(candidate.collapsed),
    customGroups: Array.isArray(candidate.customGroups) ? candidate.customGroups.filter(isCustomGroup) : []
  };
};

const knownNavGroupIds = (preference: StarGridNavGroupPreference): readonly string[] => [
  ...TEMPLATE_NAV_GROUPS.map((group) => group.id),
  ...preference.customGroups.map((group) => group.id),
  UNGROUPED_NAV_GROUP_ID
];

/** Every section id: the user's arrangement first, then any section they never moved. */
export const orderedNavGroupIds = (preference: StarGridNavGroupPreference): readonly string[] => {
  const known = knownNavGroupIds(preference);
  const arranged = preference.order.filter((id) => known.includes(id));
  return [...arranged, ...known.filter((id) => !arranged.includes(id))];
};

const navGroupResolver = (preference: StarGridNavGroupPreference): ((report: NavigationReport) => string) => {
  const known = new Set(orderedNavGroupIds(preference));
  return (report) => {
    const assigned = preference.groupByReportId[String(report.pnl_report_id)];
    // An override naming a section that no longer exists (a custom one the user deleted, or a
    // template that left TEMPLATE_ORDER) would put the report in a section nothing renders.
    return assigned !== undefined && known.has(assigned) ? assigned : defaultNavGroupId(report);
  };
};

export const navGroupIdFor = (report: NavigationReport, preference: StarGridNavGroupPreference): string => navGroupResolver(preference)(report);

const byReportName = (left: NavigationReport, right: NavigationReport): number => left.report_name.localeCompare(right.report_name);

/**
 * The reports as ordered, non-empty sections.
 *
 * Sorted by name WITHIN a section: the section header already carries the grouping, so what is left
 * to do inside it is make a long run scannable — which the API's newest-first order does not.
 */
export const groupNavReports = (reports: readonly NavigationReport[], preference: StarGridNavGroupPreference): readonly StarGridNavGroup[] => {
  const groupIdOf = navGroupResolver(preference);
  const byGroup = new Map<string, NavigationReport[]>();
  for (const report of reports) {
    const groupId = groupIdOf(report);
    const bucket = byGroup.get(groupId);
    if (bucket) bucket.push(report);
    else byGroup.set(groupId, [report]);
  }
  return orderedNavGroupIds(preference)
    .map((id) => ({ id, reports: (byGroup.get(id) ?? []).sort(byReportName) }))
    .filter((group) => group.reports.length > 0);
};

export const navGroupLabel = (groupId: string, preference: StarGridNavGroupPreference, text: StarGridText): string => {
  const template = [...TEMPLATE_NAV_GROUPS, UNGROUPED_NAV_GROUP].find((group) => group.id === groupId);
  if (template) return text(template.labelKey);
  return preference.customGroups.find((group) => group.id === groupId)?.label ?? groupId;
};

export const navGroupOptions = (preference: StarGridNavGroupPreference, text: StarGridText): readonly StarGridNavGroupOption[] =>
  orderedNavGroupIds(preference).map((id) => ({ value: id, label: navGroupLabel(id, preference, text) }));

/**
 * The preference with one report moved.
 *
 * Choosing the section the report would land in anyway REMOVES the override rather than pinning it,
 * so a report the user put back keeps following its template if that template is later re-derived.
 */
export const withNavReportGroup = (preference: StarGridNavGroupPreference, report: NavigationReport, groupId: string): StarGridNavGroupPreference => {
  const groupByReportId = { ...preference.groupByReportId };
  if (groupId === defaultNavGroupId(report)) delete groupByReportId[String(report.pnl_report_id)];
  else groupByReportId[String(report.pnl_report_id)] = groupId;
  return { ...preference, groupByReportId };
};

/** One section moved by `offset`, materialising the default order first so the first move sticks. */
export const withMovedNavGroup = (preference: StarGridNavGroupPreference, groupId: string, offset: number): StarGridNavGroupPreference => {
  const order = [...orderedNavGroupIds(preference)];
  const index = order.indexOf(groupId);
  const target = index + offset;
  if (index < 0 || target < 0 || target >= order.length) return preference;
  [order[index], order[target]] = [order[target], order[index]];
  return { ...preference, order };
};

export const withNavGroupExpanded = (preference: StarGridNavGroupPreference, groupId: string, expanded: boolean): StarGridNavGroupPreference => ({
  ...preference,
  collapsed: expanded ? preference.collapsed.filter((id) => id !== groupId) : [...new Set([...preference.collapsed, groupId])]
});

/** `custom:` plus a slug of the label, so the id stays stable across reloads and readable in storage. */
export const customNavGroupId = (label: string): string =>
  `${CUSTOM_NAV_GROUP_PREFIX}${label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}`;

/** False for a blank, unsluggable or already-taken name, which `withCustomNavGroup` would ignore. */
export const canAddCustomNavGroup = (preference: StarGridNavGroupPreference, label: string): boolean => {
  const id = customNavGroupId(label);
  return id !== CUSTOM_NAV_GROUP_PREFIX && !knownNavGroupIds(preference).includes(id);
};

export const withCustomNavGroup = (preference: StarGridNavGroupPreference, label: string): StarGridNavGroupPreference => {
  if (!canAddCustomNavGroup(preference, label)) return preference;
  const trimmed = label.trim();
  return { ...preference, customGroups: [...preference.customGroups, { id: customNavGroupId(trimmed), label: trimmed }] };
};

/** A custom section removed; its reports go back to the section they would fall in by default. */
export const withoutCustomNavGroup = (preference: StarGridNavGroupPreference, groupId: string): StarGridNavGroupPreference => {
  if (!preference.customGroups.some((group) => group.id === groupId)) return preference;
  const groupByReportId = Object.fromEntries(Object.entries(preference.groupByReportId).filter(([, id]) => id !== groupId));
  return {
    groupByReportId,
    order: preference.order.filter((id) => id !== groupId),
    collapsed: preference.collapsed.filter((id) => id !== groupId),
    customGroups: preference.customGroups.filter((group) => group.id !== groupId)
  };
};

const sameOrder = (left: readonly string[], right: readonly string[]): boolean =>
  left.length === right.length && left.every((id, index) => id === right[index]);

/**
 * The arrangement to RENDER: the user's own overrides sitting on the published section order.
 *
 * Seeding `order` rather than threading the published order through the whole algebra keeps one
 * notion of "the section order" — `orderedNavGroupIds` already treats `order` as the arrangement and
 * appends anything it does not mention (a custom section, the catch-all) after it.
 */
export const withPublishedOrder = (preference: StarGridNavGroupPreference, publishedOrder: readonly string[]): StarGridNavGroupPreference =>
  preference.order.length > 0 ? preference : { ...preference, order: [...publishedOrder] };

/**
 * The arrangement to STORE: an `order` that merely repeats the published default is dropped.
 *
 * Without this, a user who only moved a REPORT would also be storing the section order that happened
 * to be published that day, and would stop following later changes to it.
 */
export const withoutRedundantOrder = (preference: StarGridNavGroupPreference, publishedOrder: readonly string[]): StarGridNavGroupPreference =>
  sameOrder(preference.order, publishedOrder) ? { ...preference, order: [] } : preference;

export const hasNavGroupOverrides = (preference: StarGridNavGroupPreference): boolean =>
  preference.order.length > 0 ||
  preference.collapsed.length > 0 ||
  preference.customGroups.length > 0 ||
  Object.keys(preference.groupByReportId).length > 0;
